import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AppRole, useAuth } from "@/contexts/AuthContext";
import { format } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, FileDown, History, Loader2 } from "lucide-react";
import { fuzzyScore } from "@/lib/fuzzyScore";
import { exportToCsv } from "@/lib/exportCsv";
import { SessionHistoryDetailDialog } from "./SessionHistoryDetailDialog";
import { RemedialSessionDialog } from "./RemedialSessionDialog";

const PAGE_SIZE = 50;

interface ClosedSession {
  id: string;
  module_session_id: string | null;
  module_code: string | null;
  class: string | null;
  topic: string | null;
  session_date: string;
  opened_at: string | null;
  closed_at: string | null;
  status: string;
  remedial_parent_id?: string | null;
}

interface ModuleSessionRow {
  id: string;
  semester: string;
  class: string;
  module_code: string;
  module_name: string | null;
  session_no: number;
  session_name: string;
  session_kind: string;
}

interface SummaryRow {
  session_id: string;
  total_enrolled: number | null;
  present_count: number | null;
  pending_count: number | null;
  absent_count: number | null;
  cr_recorded_count: number | null;
  attendance_rate: number | null;
  generated_at: string | null;
  generated_by: string | null;
}

const CR_ROLES = [
  "cr_cmt4", "cr_cmt5", "cr_cmt6",
  "cr_pst4", "cr_pst5", "cr_pst6",
  "cr_sw4", "cr_sw5", "cr_sw6",
] as const satisfies readonly AppRole[];

function formatDuration(openedAt: string | null, closedAt: string | null) {
  if (!openedAt || !closedAt) return "—";
  const diffMs = new Date(closedAt).getTime() - new Date(openedAt).getTime();
  if (diffMs <= 0) return "—";
  const mins = Math.floor(diffMs / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function SessionHistoryView() {
  const { user, hasRole } = useAuth();
  const isClassRep = useMemo(() => CR_ROLES.some((r) => hasRole(r)), [hasRole]);
  const isStudent = hasRole("student");
  const canRequestRemedial = hasRole("head_academic") || hasRole("superadmin");

  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState<ClosedSession[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [moduleSessions, setModuleSessions] = useState<Record<string, ModuleSessionRow>>({});
  const [summaries, setSummaries] = useState<Record<string, SummaryRow>>({});

  const [filterSemester, setFilterSemester] = useState<string>("all");
  const [filterClass, setFilterClass] = useState<string>("all");
  const [filterModule, setFilterModule] = useState<string>("all");
  const [filterFrom, setFilterFrom] = useState<string>("");
  const [filterTo, setFilterTo] = useState<string>("");
  const [search, setSearch] = useState("");

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [detailSession, setDetailSession] = useState<ClosedSession | null>(null);
  const [detailModuleSession, setDetailModuleSession] = useState<ModuleSessionRow | null>(null);
  const [detailSummary, setDetailSummary] = useState<SummaryRow | null>(null);

  // Class restriction for class reps — derived from their roles
  const classRepClasses = useMemo(() => {
    if (!isClassRep) return null;
    const classes: string[] = [];
    if (hasRole("cr_cmt4")) classes.push("CMT 04");
    if (hasRole("cr_cmt5")) classes.push("CMT 05");
    if (hasRole("cr_cmt6")) classes.push("CMT 06");
    if (hasRole("cr_pst4")) classes.push("PST 04");
    if (hasRole("cr_pst5")) classes.push("PST 05");
    if (hasRole("cr_pst6")) classes.push("PST 06");
    if (hasRole("cr_sw4")) classes.push("SW 04");
    if (hasRole("cr_sw5")) classes.push("SW 05");
    if (hasRole("cr_sw6")) classes.push("SW 06");
    return classes;
  }, [isClassRep, hasRole]);

  useEffect(() => {
    if (isStudent) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      let q = supabase
        .from("attendance_sessions")
        .select("id, module_session_id, module_code, class, topic, session_date, opened_at, closed_at, status, remedial_parent_id", { count: "exact" })
        .eq("status", "closed")
        .order("closed_at", { ascending: false, nullsFirst: false })
        .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

      if (filterClass !== "all") q = q.eq("class", filterClass);
      if (filterModule !== "all") q = q.eq("module_code", filterModule);
      if (filterFrom) q = q.gte("closed_at", `${filterFrom}T00:00:00`);
      if (filterTo) q = q.lte("closed_at", `${filterTo}T23:59:59`);
      if (classRepClasses && classRepClasses.length) q = q.in("class", classRepClasses);

      const { data, count } = await q;
      if (cancelled) return;
      const rows = (data ?? []) as ClosedSession[];
      setSessions(rows);
      setTotalCount(count ?? 0);

      const msIds = Array.from(new Set(rows.map((r) => r.module_session_id).filter(Boolean))) as string[];
      const sessIds = rows.map((r) => r.id);

      const [msRes, sumRes] = await Promise.all([
        msIds.length
          ? supabase
              .from("module_sessions")
              .select("id, semester, class, module_code, module_name, session_no, session_name, session_kind")
              .in("id", msIds)
          : Promise.resolve({ data: [] as ModuleSessionRow[] }),
        sessIds.length
          ? supabase
              .from("session_summaries")
              .select("session_id, total_enrolled, present_count, pending_count, absent_count, cr_recorded_count, attendance_rate, generated_at, generated_by")
              .in("session_id", sessIds)
          : Promise.resolve({ data: [] as SummaryRow[] }),
      ]);

      if (cancelled) return;
      const msMap: Record<string, ModuleSessionRow> = {};
      ((msRes.data ?? []) as ModuleSessionRow[]).forEach((m) => { msMap[m.id] = m; });
      setModuleSessions(msMap);

      const sMap: Record<string, SummaryRow> = {};
      ((sumRes.data ?? []) as SummaryRow[]).forEach((s) => { sMap[s.session_id] = s; });
      setSummaries(sMap);

      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [page, filterClass, filterModule, filterFrom, filterTo, classRepClasses, isStudent]);

  // Derive filter dropdown options from current page's module sessions
  const semesterOptions = useMemo(() => {
    const set = new Set<string>();
    Object.values(moduleSessions).forEach((m) => m.semester && set.add(m.semester));
    return Array.from(set).sort();
  }, [moduleSessions]);

  const classOptions = useMemo(() => {
    const set = new Set<string>();
    sessions.forEach((s) => s.class && set.add(s.class));
    return Array.from(set).sort();
  }, [sessions]);

  const moduleOptions = useMemo(() => {
    const set = new Set<string>();
    sessions.forEach((s) => s.module_code && set.add(s.module_code));
    return Array.from(set).sort();
  }, [sessions]);

  // Apply client-side semester + search filters (semester needs join)
  const visibleSessions = useMemo(() => {
    const q = search.trim();
    return sessions.filter((s) => {
      if (filterSemester !== "all") {
        const ms = s.module_session_id ? moduleSessions[s.module_session_id] : null;
        if (!ms || ms.semester !== filterSemester) return false;
      }
      if (q) {
        const ms = s.module_session_id ? moduleSessions[s.module_session_id] : null;
        const haystack = `#${ms?.session_no ?? ""} ${ms?.session_name ?? ""} ${s.topic ?? ""} ${s.module_code ?? ""}`;
        if (fuzzyScore(haystack, q) == null) return false;
      }
      return true;
    });
  }, [sessions, filterSemester, search, moduleSessions]);

  // Group by module_session_id (or "uncatalogued")
  const groups = useMemo(() => {
    const map = new Map<string, { key: string; ms: ModuleSessionRow | null; runs: ClosedSession[] }>();
    visibleSessions.forEach((s) => {
      const key = s.module_session_id ?? `__uncat__${s.module_code ?? "none"}__${s.class ?? "none"}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          ms: s.module_session_id ? moduleSessions[s.module_session_id] ?? null : null,
          runs: [],
        });
      }
      map.get(key)!.runs.push(s);
    });
    // Sort: catalogued first by module/session_no, then uncatalogued
    return Array.from(map.values()).sort((a, b) => {
      const aUn = a.key.startsWith("__uncat__") ? 1 : 0;
      const bUn = b.key.startsWith("__uncat__") ? 1 : 0;
      if (aUn !== bUn) return aUn - bUn;
      if (a.ms && b.ms) {
        return (a.ms.module_code || "").localeCompare(b.ms.module_code || "")
          || (a.ms.session_no || 0) - (b.ms.session_no || 0);
      }
      return 0;
    });
  }, [visibleSessions, moduleSessions]);

  function exportCsv() {
    const rows: string[][] = [];
    visibleSessions.forEach((s) => {
      const ms = s.module_session_id ? moduleSessions[s.module_session_id] : null;
      const sum = summaries[s.id];
      rows.push([
        ms?.semester ?? "",
        s.class ?? "",
        s.module_code ?? "",
        ms ? `#${ms.session_no} ${ms.session_name}` : (s.topic ?? ""),
        s.session_date,
        s.opened_at ? new Date(s.opened_at).toLocaleString() : "",
        s.closed_at ? new Date(s.closed_at).toLocaleString() : "",
        formatDuration(s.opened_at, s.closed_at),
        String(sum?.present_count ?? 0),
        String(sum?.total_enrolled ?? 0),
        sum?.attendance_rate != null ? `${(Number(sum.attendance_rate) * 100).toFixed(1)}%` : "",
      ]);
    });
    exportToCsv(
      `Session_History_${new Date().toISOString().slice(0, 10)}.csv`,
      ["Semester", "Class", "Module", "Session", "Date", "Opened", "Closed", "Duration", "Present", "Enrolled", "Rate"],
      rows,
    );
  }

  function openDetail(s: ClosedSession) {
    const ms = s.module_session_id ? moduleSessions[s.module_session_id] ?? null : null;
    const sum = summaries[s.id] ?? null;
    setDetailSession(s);
    setDetailModuleSession(ms);
    setDetailSummary(sum);
  }

  if (isStudent) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          Session history is not available to students.
        </CardContent>
      </Card>
    );
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="py-4 grid gap-3 md:grid-cols-6">
          <div className="md:col-span-1">
            <Label className="text-xs">Semester</Label>
            <Select value={filterSemester} onValueChange={setFilterSemester}>
              <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {semesterOptions.map((s) => <SelectItem key={s} value={s}>Semester {s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-1">
            <Label className="text-xs">Class</Label>
            <Select value={filterClass} onValueChange={(v) => { setFilterClass(v); setPage(0); }}>
              <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {classOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-1">
            <Label className="text-xs">Module</Label>
            <Select value={filterModule} onValueChange={(v) => { setFilterModule(v); setPage(0); }}>
              <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {moduleOptions.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-1">
            <Label className="text-xs">From</Label>
            <Input type="date" value={filterFrom} onChange={(e) => { setFilterFrom(e.target.value); setPage(0); }} />
          </div>
          <div className="md:col-span-1">
            <Label className="text-xs">To</Label>
            <Input type="date" value={filterTo} onChange={(e) => { setFilterTo(e.target.value); setPage(0); }} />
          </div>
          <div className="md:col-span-1 flex items-end">
            <Button variant="outline" size="sm" className="w-full" onClick={exportCsv} disabled={!visibleSessions.length}>
              <FileDown className="w-4 h-4 mr-1" /> Export CSV
            </Button>
          </div>
          <div className="md:col-span-6">
            <Label className="text-xs">Search session number, name, topic or module</Label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="e.g. crd vs, #2, intro, anatomy" />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading session history…</CardContent></Card>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No closed sessions match the current filters.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {groups.map((g) => {
            const isOpen = openGroups[g.key] ?? true;
            const primaryRemedialCandidate = g.runs.find((run) => !run.remedial_parent_id) ?? null;
            const headerLabel = g.ms
              ? `${g.ms.class} · Sem ${g.ms.semester} · ${g.ms.module_code}${g.ms.module_name ? ` — ${g.ms.module_name}` : ""}`
              : `Uncatalogued · ${g.runs[0]?.module_code ?? "—"} · ${g.runs[0]?.class ?? "—"}`;
            const subLabel = g.ms
              ? `Session #${g.ms.session_no} ${g.ms.session_name}`
              : "One-off / legacy sessions";
            return (
              <Collapsible key={g.key} open={isOpen} onOpenChange={(o) => setOpenGroups((s) => ({ ...s, [g.key]: o }))}>
                <Card>
                  <CollapsibleTrigger asChild>
                    <button className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-muted/50 transition-colors rounded-t-lg">
                      {isOpen ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{headerLabel}</p>
                        <p className="text-xs text-muted-foreground truncate">{subLabel}</p>
                      </div>
                      <Badge variant="secondary" className="shrink-0">{g.runs.length} run{g.runs.length === 1 ? "" : "s"}</Badge>
                      {canRequestRemedial && primaryRemedialCandidate && (
                        <span onClick={(e) => e.stopPropagation()} className="shrink-0">
                          <RemedialSessionDialog session={primaryRemedialCandidate} />
                        </span>
                      )}
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="border-t overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Opened</TableHead>
                            <TableHead>Closed</TableHead>
                            <TableHead>Duration</TableHead>
                            <TableHead>Pres / Enr</TableHead>
                            <TableHead>Pending</TableHead>
                            <TableHead>Absent</TableHead>
                            <TableHead>Rate</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {g.runs.map((s) => {
                            const sum = summaries[s.id];
                            const rate = sum?.attendance_rate != null
                              ? `${(Number(sum.attendance_rate) * 100).toFixed(0)}%`
                              : "—";
                            return (
                              <TableRow key={s.id} className="cursor-pointer" onClick={() => openDetail(s)}>
                                <TableCell className="whitespace-nowrap">{format(new Date(s.session_date + "T00:00:00"), "dd MMM yy")}</TableCell>
                                <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{s.opened_at ? format(new Date(s.opened_at), "HH:mm") : "—"}</TableCell>
                                <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{s.closed_at ? format(new Date(s.closed_at), "HH:mm") : "—"}</TableCell>
                                <TableCell className="whitespace-nowrap text-xs">{formatDuration(s.opened_at, s.closed_at)}</TableCell>
                                <TableCell className="whitespace-nowrap">{sum ? `${sum.present_count ?? 0} / ${sum.total_enrolled ?? 0}` : "—"}</TableCell>
                                <TableCell className="whitespace-nowrap">{sum?.pending_count ?? 0}</TableCell>
                                <TableCell className="whitespace-nowrap">{sum?.absent_count ?? 0}</TableCell>
                                <TableCell className="whitespace-nowrap font-medium">{rate}</TableCell>
                                <TableCell className="whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                                  {!s.remedial_parent_id && <RemedialSessionDialog session={s} />}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalCount > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {page + 1} of {totalPages} · {totalCount} closed session{totalCount === 1 ? "" : "s"}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      <SessionHistoryDetailDialog
        session={detailSession}
        moduleSession={detailModuleSession}
        summary={detailSummary}
        onClose={() => { setDetailSession(null); setDetailModuleSession(null); setDetailSummary(null); }}
      />
    </div>
  );
}
